import { Button, Group, Icon, Label, Radio, Stack } from "@/components"
import { AccordionWithOptions } from "@/components/AccordionWithOptions"
import AddTextItemsToListInput from "@/components/Controlled/AddTextItemsToListInput/AddTextItemsToListInput"
import { enumOrderSort } from "@/helpers/enums"
import {
    getKebabMenuOptions,
    getMessageFieldName,
    getMessageVariationFieldName,
} from "@/pages/campaign-form/components/campaigns/writeALetter/Message.helper"
import { TwoColumnsLayout } from "@/pages/campaign-form/components/layouts/TwoColumnsLayout/TwoColumnsLayout"
import { useStyles } from "@/pages/campaign-form/components/sections/message/MessageGroupSection/MessageGroupSection.styles"

import { MessageVariationSection } from "@/pages/campaign-form/components/sections/message/MessageVariationSection/MessageVariationSection"
import { TargetedOfficialsSelectionBlocked } from "@/pages/campaign-form/components/sections/message/TargetedOfficialsSelectionBlocked/TargetedOfficialsSelectionBlocked"
import { TargetedOfficialsSelectionWriteLetter } from "@/pages/campaign-form/components/sections/message/TargetedOfficialsSelectionWriteLetter/TargetedOfficialsSelectionWriteLetter"
import { CampaignFormValues } from "@/pages/campaign-form/types"
import { fetchMessageVariations } from "@/services/campaigns"
import { CampaignMessageGroup } from "@/types/djangio"
import { Select } from "@mantine/core"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { UseFieldArrayReturn, useFormContext } from "react-hook-form"
import { hasAnyPermission } from "shared/imports/permissionFunctions"
import { emailValidation } from "shared/imports/validationFunctions"
import { isCustomTargetingEnabled } from "@/pages/campaign-form/helpers"

const { DraftOptions, MessageVariationsType, MessageVariationGenerationAiType } = DjangIO.app.grassroots.campaign.types

const MAX_VARIATIONS = 50
const DEFAULT_AMOUNT_OF_VARIATIONS = 10

interface MessageGroupSectionProps {
    index: number
    setOpenedItem: (value: string) => void
    message: CampaignMessageGroup
    fieldArray: UseFieldArrayReturn
    prefix?: string
}

export const MessageGroupSection = ({
    index,
    setOpenedItem,
    message,
    fieldArray,
    prefix = null,
}: MessageGroupSectionProps) => {
    const { classes } = useStyles()
    const formContext = useFormContext<CampaignFormValues>()
    const { fields, update, remove, append } = fieldArray

    // TODO: This can be defined on MessageVariationsType, and then filtered by satisfies_permissions()
    const isAiEnabled = hasAnyPermission([DjangIO.app.models.PermissionType.ai_outbox_suggestions])

    // Block the user from editing the targeting until all targeting is reset
    const isCampaignTargetingLegacyData = (message: CampaignMessageGroup) => {
        if (message.legacy_exclude_message_group) {
            return Boolean(message.legacy_exclude_message_group)
        }
        if (message.legacy_base_searches) {
            return message.legacy_base_searches.length
        }
        return false
    }

    const getOfficialsTargetingSection = (message: CampaignMessageGroup, index: number): JSX.Element =>
        isCampaignTargetingLegacyData(message) ? (
            <TargetedOfficialsSelectionBlocked messageIndex={index} prefix={prefix} />
        ) : (
            <TargetedOfficialsSelectionWriteLetter messageIndex={index} prefix={prefix} />
        )

    const getRealGroupIndex = (index: number) => {
        let archivedAmountBeforeIndex = 0
        for (let i = 0; i < index; i++) {
            // @ts-expect-error
            if (fields[i].archived === true) {
                archivedAmountBeforeIndex++
            }
        }

        return index - archivedAmountBeforeIndex
    }

    const selectItems = useMemo(
        () => Array.from({ length: MAX_VARIATIONS }, (_, index) => ({ value: `${index + 1}`, label: `${index + 1}` })),
        [],
    )
    const messageVariationsCount = message?.message_variations?.length?.toString()
    const [numberOfVariations, setNumberOfVariations] = useState<string>(messageVariationsCount)
    const [currentMessageIndex, setCurrentMessageIndex] = useState<number>(1)

    const handleChangeSetNumberOfVariations = (value: string) => {
        const difference = Number(value) - Number(numberOfVariations)

        if (difference > 0) {
            const variations = formContext.getValues(getMessageFieldName(index, "message_variations", prefix))
            let lastVariationIndex = variations[variations.length - 1].index
            const addedVariations = []

            for (let i = 0; i < difference; i++) {
                const newVariation = {
                    subject: "",
                    content: "",
                    fixed_pre_content: "",
                    fixed_post_content: "",
                    archived: false,
                    index: lastVariationIndex + 1,
                    ai_generation_type: null,
                }

                addedVariations.push(newVariation)
                lastVariationIndex++
            }
            formContext.setValue(getMessageFieldName(index, "message_variations", prefix), [
                ...variations,
                ...addedVariations,
            ])
            setCurrentMessageIndex(variations.length + difference)
        } else if (difference < 0) {
            const variations = formContext.getValues(getMessageFieldName(index, "message_variations", prefix))

            const toArchive = []
            for (let i = Math.abs(difference); i > 0; i--) {
                if (variations[variations.length - 1].id) {
                    toArchive.push({ ...variations[variations.length - 1], archived: true })
                }

                variations.pop()
            }

            const currentlyArchivedVariations = formContext.getValues(
                getMessageFieldName(index, "archived_variations", prefix),
            )
            const archivedVariations = [...(currentlyArchivedVariations || []), ...toArchive]

            formContext.setValue(getMessageFieldName(index, "message_variations", prefix), variations)
            formContext.setValue(getMessageFieldName(index, "archived_variations", prefix), archivedVariations)

            if (currentMessageIndex > Number(value)) {
                setCurrentMessageIndex(1)
            }
        }
        setNumberOfVariations(value)
    }

    const messageVariationsType = formContext.watch(getMessageFieldName(index, "message_variations_type", prefix))
    const prevMessageVariationsType = useRef()
    const numberOfMessagesLabel = useMemo(() => {
        return messageVariationsType === MessageVariationsType.custom_written.value ? "custom" : "auto-generated"
    }, [messageVariationsType])

    useEffect(() => {
        prevMessageVariationsType.current = messageVariationsType
    }, [messageVariationsType])

    const handleChangeMessageVariationsType = (value: string) => {
        if (MessageVariationsType.none.value.toString() === value) {
            handleChangeSetNumberOfVariations("1")
        } else if (MessageVariationsType.none.value === prevMessageVariationsType.current) {
            handleChangeSetNumberOfVariations(`${DEFAULT_AMOUNT_OF_VARIATIONS}`)
        }
    }

    const shouldAutoGeneratedBeDisabled = () => {
        const firstMessageVariation = formContext.getValues(getMessageFieldName(index, "message_variations", prefix))[0]

        return !firstMessageVariation?.content || !firstMessageVariation?.subject
    }

    useEffect(() => {
        if (shouldAutoGeneratedBeDisabled() && messageVariationsType === MessageVariationsType.auto_generated.value) {
            formContext.setValue(
                getMessageFieldName(index, "message_variations_type", prefix),
                MessageVariationsType.custom_written.value,
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldAutoGeneratedBeDisabled])

    const [showOverlay, setOverlay] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const generateMessagesUsingAI = async () => {
        const messages = formContext.getValues(getMessageFieldName(index, "message_variations", prefix))
        const { subject, content, fixed_pre_content, fixed_post_content } = messages[0]
        const advocate_editing_permission = formContext.getValues(
            getMessageFieldName(index, "advocate_editing_permission", prefix),
        )

        try {
            setOverlay(true)
            setErrorMessage("")
            const generatedVariations = await fetchMessageVariations(
                subject,
                content,
                fixed_pre_content,
                fixed_post_content,
                messages.length - 1,
            )
            generatedVariations.forEach((variation, variation_index) => {
                const { subject, content, fixed_pre_content, fixed_post_content } = variation

                formContext.setValue(
                    getMessageVariationFieldName(index, variation_index + 1, "ai_generation_type", prefix),
                    MessageVariationGenerationAiType.ai_generated.value,
                )
                formContext.setValue(
                    getMessageVariationFieldName(index, variation_index + 1, "subject", prefix),
                    subject,
                )
                formContext.setValue(
                    getMessageVariationFieldName(index, variation_index + 1, "content", prefix),
                    content,
                )
                if (advocate_editing_permission === DraftOptions.partial_edit.value) {
                    formContext.setValue(
                        getMessageVariationFieldName(index, variation_index + 1, "fixed_pre_content", prefix),
                        fixed_pre_content,
                    )
                    formContext.setValue(
                        getMessageVariationFieldName(index, variation_index + 1, "fixed_post_content", prefix),
                        fixed_post_content,
                    )
                }
            })
        } catch (e) {
            console.error(e)
            setErrorMessage(`We had a problem loading all ${numberOfVariations} messages; please try again.`)
        } finally {
            setOverlay(false)
        }
    }

    return (
        <AccordionWithOptions
            title={`Message ${index + 1}`}
            titleName={getMessageFieldName(index, "name", prefix)}
            // @ts-expect-error
            options={getKebabMenuOptions(index, fields, formContext, update, remove, append, setOpenedItem, prefix)}
            itemId={`${index + 1}`}
        >
            <TwoColumnsLayout columnWidth={540}>
                <Stack className={classes.leftColumn} spacing="xl">
                    {!isCustomTargetingEnabled(formContext) && (
                        <Radio.Group
                            name={getMessageFieldName(index, "has_custom_email_targets", prefix)}
                            label={`Choose recipients for message ${getRealGroupIndex(index + 1)}`}
                            transform={{
                                input: (input: boolean) => String(input),
                                output: (output: string) => (output === "true" ? true : false),
                            }}
                        >
                            <Group>
                                <Radio value={"false"} label="Officials" />
                                <Radio value={"true"} label="Custom recipients" />
                            </Group>
                        </Radio.Group>
                    )}
                    {formContext.watch(getMessageFieldName(index, "has_custom_email_targets", prefix)) === true ? (
                        <AddTextItemsToListInput
                            name={getMessageFieldName(index, "custom_email_targets", prefix)}
                            label="Enter email address (10 maximum)"
                            placeholder="Enter email address here"
                            maxItems={10}
                            rules={{
                                required: "Add at least one email",
                            }}
                            transform={{
                                output: (output: string) => output.toLowerCase(),
                            }}
                            filters={{
                                inputValidate: emailValidation,
                            }}
                        />
                    ) : (
                        getOfficialsTargetingSection(message, index)
                    )}
                    <Radio.Group
                        name={getMessageFieldName(index, "advocate_editing_permission", prefix)}
                        label="Advocate editing permissions"
                        transform={{
                            input: (input: string | number) => Number(input),
                            output: (output: string) => Number(output),
                        }}
                        onChange={() => formContext.clearErrors()}
                    >
                        <Group>
                            {DraftOptions.items()
                                .sort(enumOrderSort)
                                .map((option, index) => {
                                    return (
                                        <Radio
                                            key={`${option.value}-${index}`}
                                            value={option.value}
                                            label={option.label}
                                        />
                                    )
                                })}
                        </Group>
                    </Radio.Group>
                    <Radio.Group
                        name={getMessageFieldName(index, "message_variations_type", prefix)}
                        label="Message variations"
                        transform={{
                            input: (input: string | number) => Number(input),
                            output: (output: string) => Number(output),
                        }}
                        onChange={handleChangeMessageVariationsType}
                        tooltip="Create multiple versions of messages to be sent. Only when you have added messages can you choose to auto-generate variations."
                    >
                        <Group>
                            {MessageVariationsType.items()
                                .sort(enumOrderSort)
                                .map((option, index) => {
                                    if (!isAiEnabled && option.value === MessageVariationsType.auto_generated.value) {
                                        return null
                                    }

                                    return (
                                        <Radio
                                            key={`${option.value}-${index}`}
                                            value={option.value}
                                            label={
                                                option.value === MessageVariationsType.auto_generated.value ? (
                                                    <>
                                                        <Icon size="sm" icon="sparkles" iconFamily="fas" />
                                                        {option.label}
                                                    </>
                                                ) : (
                                                    option.label
                                                )
                                            }
                                            disabled={
                                                option.value === MessageVariationsType.auto_generated.value &&
                                                shouldAutoGeneratedBeDisabled()
                                            }
                                        />
                                    )
                                })}
                        </Group>
                    </Radio.Group>
                    {messageVariationsType !== MessageVariationsType.none.value && (
                        <div className={classes.numberCustomMessageWrapper}>
                            <Select
                                label={<Label label={`Number of ${numberOfMessagesLabel} messages`} tooltip="TBD" />}
                                data={selectItems}
                                value={numberOfVariations}
                                onChange={handleChangeSetNumberOfVariations}
                                wrapperProps={{ className: classes.numberCustomMessageSelect }}
                            />
                            {messageVariationsType === MessageVariationsType.auto_generated.value && (
                                <Button
                                    className={classes.generateButton}
                                    onClick={async () => await generateMessagesUsingAI()}
                                    variant="ghost"
                                    size="md"
                                    leftIcon={<Icon icon="sync-alt" iconFamily="fas" />}
                                >
                                    Generate
                                </Button>
                            )}
                        </div>
                    )}
                </Stack>
                <MessageVariationSection
                    messageIndex={index}
                    setCurrentMessageIndex={setCurrentMessageIndex}
                    currentMessageIndex={currentMessageIndex}
                    setNumberOfVariations={setNumberOfVariations}
                    showOverlay={showOverlay}
                    setOverlay={setOverlay}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    prefix={prefix}
                />
            </TwoColumnsLayout>
        </AccordionWithOptions>
    )
}
